import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import QueryKeys from '@constants/query-keys';
import { useModalState } from '@hooks';
import { successMessage } from '@services';
import { CreateMonitoringPayload, UpdateMonitoringPayload } from './types';
import { ApiClientEnterprise } from '@helpers/apiClients';

export const useMonitoring = () => {
  const defaultItem: CreateMonitoringPayload = {
    title: '',
    photoUrl: '',
    status: 0,
    category: 0,
    priority: 0,
  };

  const { id: organizationId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [editingItem, setEditingItem] = useState<CreateMonitoringPayload>(defaultItem);
  const [isOpenModal, , openModal, closeModal] = useModalState(false);

  const onRefetch = () => {
    queryClient.invalidateQueries({
      queryKey: QueryKeys.enterpriseMonitoring,
    });
  };

  const { mutate: deleteMonitoring } = useMutation<unknown, unknown, string[]>(
    (uuids) =>
      ApiClientEnterprise.delete(`/enterprises-admin/${organizationId}/assets`, {
        // TODO: Replace with the actual API endpoint for deleting monitoring items
        data: { sentimentAnalysisUuids: uuids },
      }),
    {
      onSuccess: () => {
        successMessage('Monitoring item has been removed!');
        onRefetch();
        setEditingItem(defaultItem);
      },
    },
  );

  const { mutate: createMonitoring, isLoading: isCreating } = useMutation({
    mutationFn: (data: CreateMonitoringPayload) =>
      ApiClientEnterprise.post(`/enterprises-admin/${organizationId}/assets`, {
        ...data,
        category: Number(data.category),
        status: Number(data.status),
      }),
    onSuccess: () => {
      successMessage('Monitoring item created successfully!');
      onRefetch();
      setEditingItem(defaultItem);
    },
  });

  const { mutate: editMonitoring, isLoading: isEditing } = useMutation({
    mutationFn: (data: UpdateMonitoringPayload) =>
      ApiClientEnterprise.put(`/enterprises-admin/${organizationId}/assets/${data.id}`, {
        ...data,
        category: Number(data.category),
        status: Number(data.status),
      }),
    onSuccess: () => {
      successMessage('Monitoring item updated successfully!');
      onRefetch();
      setEditingItem(defaultItem);
    },
  });

  const onEdit = (row: any) => setEditingItem(row);

  const onDelete = (row: any) => {
    deleteMonitoring([row.id]);
  };

  return {
    isOpenModal,
    editingItem,
    defaultItem,
    isCreating,
    isEditing,
    openModal,
    closeModal,
    setEditingItem,
    onEdit,
    onDelete,
    createMonitoring,
    editMonitoring,
  };
};
